import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
} from '@mui/material';
import { pricingApi } from '../services/api';

const CombinedFuelPricing = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  const loadBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const data = await pricingApi.listCombinedFuelBatches();
      setBatches(data.batches || []);
    } catch (e) {
      setError(e.message || 'Failed to load import history');
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const handleUpload = async () => {
    if (!file) {
      setError('Choose a .csv file first');
      return;
    }
    setUploading(true);
    setError(null);
    setMessage(null);
    setLastResult(null);
    try {
      const result = await pricingApi.uploadCombinedFuelCsv(file);
      setLastResult(result);
      setMessage(
        `Uploaded ${result.rowCount} rows — ${result.promote?.promoted ?? 0} promoted to core, ` +
          `${result.locationEnrich?.updated ?? 0} enriched from master locations, ` +
          `${result.promote?.rejected ?? 0} rejected, ${result.archive?.archived ?? 0} archived.`
      );
      setFile(null);
      await loadBatches();
    } catch (e) {
      const timedOut =
        e.code === 'ECONNABORTED' || String(e.message || '').toLowerCase().includes('timeout');
      if (timedOut) {
        await loadBatches();
        setError(null);
        setMessage(
          'The upload took longer than expected, but it may have completed successfully. ' +
            'Check Import history below — if the latest row shows status "completed", your file was processed.'
        );
      } else {
        setError(e.message || 'Upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        Combined fuel prices
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Upload the daily combined CSV (all providers). Rows load into{' '}
        <code>northstar.fuel_prices_staging</code>, valid rows promote to{' '}
        <code>fuel_prices</code>, missing location fields are filled from{' '}
        <code>ref_master_locations</code>, and prices older than 2 days move to{' '}
        <code>fuel_prices_archive</code>. If multiple files are uploaded the same day, the most
        recent import wins for duplicate station/product/provider/date keys. Large files may take
        1–2 minutes — keep this page open until the summary appears.
      </Typography>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Upload combined CSV
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <Button variant="outlined" component="label" disabled={uploading}>
            Select file
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>
          <Typography variant="body2">{file ? file.name : 'No file selected'}</Typography>
          <Button variant="contained" onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? <CircularProgress size={22} color="inherit" /> : 'Upload File'}
          </Button>
        </Box>
      </Paper>

      {lastResult && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Last import summary
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {(lastResult.providerCounts &&
              Object.entries(lastResult.providerCounts).map(([provider, count]) => (
                <Chip key={provider} label={`${provider}: ${count}`} size="small" />
              ))) ||
              null}
          </Box>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>Rows in file</TableCell>
                <TableCell>{lastResult.rowCount}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Staged</TableCell>
                <TableCell>{lastResult.staging?.inserted ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Promoted to core</TableCell>
                <TableCell>{lastResult.promote?.promoted ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Location fields enriched</TableCell>
                <TableCell>{lastResult.locationEnrich?.updated ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Rejected (validation)</TableCell>
                <TableCell>{lastResult.promote?.rejected ?? '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Archived (&gt;2 days)</TableCell>
                <TableCell>{lastResult.archive?.archived ?? '—'}</TableCell>
              </TableRow>
              {lastResult.effectiveDates?.length > 0 && (
                <TableRow>
                  <TableCell>Effective dates</TableCell>
                  <TableCell>{lastResult.effectiveDates.join(', ')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {lastResult.warnings?.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {lastResult.warnings.join('; ')}
            </Alert>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1">Import history</Typography>
          <Button onClick={loadBatches} disabled={loadingBatches}>
            Refresh
          </Button>
        </Box>
        {loadingBatches ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell align="right">Rows</TableCell>
                  <TableCell align="right">Promoted</TableCell>
                  <TableCell align="right">Rejected</TableCell>
                  <TableCell align="right">Archived</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No imports yet
                    </TableCell>
                  </TableRow>
                )}
                {batches.map((b) => (
                  <TableRow key={b._id}>
                    <TableCell>{b.fileName}</TableCell>
                    <TableCell>{new Date(b.uploadedAt).toLocaleString()}</TableCell>
                    <TableCell align="right">{b.rowCount ?? '—'}</TableCell>
                    <TableCell align="right">{b.promote?.promoted ?? '—'}</TableCell>
                    <TableCell align="right">{b.promote?.rejected ?? '—'}</TableCell>
                    <TableCell align="right">{b.archive?.archived ?? '—'}</TableCell>
                    <TableCell>{b.status ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default CombinedFuelPricing;
